import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  CalendarClock,
  Car,
  DollarSign,
  LayoutDashboard,
  MapPin,
  Radio,
  LifeBuoy,
  Settings as SettingsIcon,
  ShieldCheck,
  Ticket,
  Trophy,
  UserCog,
  Users as UsersIcon,
} from "lucide-react";

export interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /**
   * Pages no role-created-by-SUPER_ADMIN can ever grant — currently only the
   * Team & Roles group itself, since it's what edits every other role's
   * access and can't sensibly be part of what it configures.
   */
  superAdminOnly?: boolean;
  /** Sub-pages shown as a collapsible group under this item. */
  children?: { path: string; label: string }[];
}

/**
 * Single source of truth for the sidebar. Real visibility for every page
 * except the SUPER_ADMIN-only ones is resolved dynamically per admin via
 * `GET /admin/my-page-access` (see useMyPageAccess) — a SUPER_ADMIN assigns
 * each page to whichever custom roles they create on the Roles page. This
 * list exists to name/order/icon every page that CAN be granted, and to
 * mark the handful that can't be.
 */
export const NAV_ITEMS: NavItem[] = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/scheduled-rides", label: "Scheduled Rides", icon: CalendarClock },
  { path: "/live-rides", label: "Live Rides", icon: Radio },
  { path: "/rides", label: "Rides", icon: Car },
  { path: "/drivers", label: "Drivers", icon: Car },
  { path: "/driver-approvals", label: "Driver Approvals", icon: ShieldCheck },
  { path: "/users", label: "Users", icon: UsersIcon },
  { path: "/rankings", label: "Driver Rankings", icon: Trophy },
  { path: "/commission", label: "Commission", icon: DollarSign },
  { path: "/fares", label: "Fares", icon: DollarSign },
  { path: "/zones", label: "Service Zones", icon: MapPin },
  { path: "/promos", label: "Promos & Coupons", icon: Ticket },
  { path: "/support", label: "Support", icon: LifeBuoy },
  { path: "/reports", label: "Reports", icon: BarChart3 },
  { path: "/settings", label: "Settings", icon: SettingsIcon },
  {
    path: "/team",
    label: "Team & Roles",
    icon: UserCog,
    superAdminOnly: true,
    children: [
      { path: "/team/admins", label: "Admins" },
      { path: "/team/roles", label: "Roles" },
    ],
  },
];

/** Every page a custom role can actually be granted — everything except the SUPER_ADMIN-only group's own pages. */
export const GRANTABLE_PAGES: { path: string; label: string }[] = NAV_ITEMS.filter(
  (item) => !item.superAdminOnly,
).map(({ path, label }) => ({ path, label }));

/**
 * Whether the calling admin can see the page at `path`. SUPER_ADMIN always
 * passes and is the only one who can ever see a `superAdminOnly` page.
 * Everyone else is decided purely by `myPages` (their resolved
 * `GET /admin/my-page-access` result) — while that's still loading,
 * `myPages` is undefined and this returns false, since there is no longer
 * a meaningful static default to fall back to once roles are fully custom.
 */
export function canAccessPage(path: string, isSuperAdmin: boolean, myPages: string[] | undefined): boolean {
  if (isSuperAdmin) return true;
  const item = NAV_ITEMS.find((n) => n.path === path || n.children?.some((c) => c.path === path));
  if (!item || item.superAdminOnly) return false;
  return myPages?.includes(path) ?? false;
}
